import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "space-y-1" }
const _hoisted_2 = { class: "block text-sm font-medium text-gray-700" }
const _hoisted_3 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = ["placeholder", "maxlength"]
const _hoisted_6 = {
  key: 0,
  class: "text-sm text-red-500 mt-2 flex items-center"
}
const _hoisted_7 = {
  key: 1,
  class: "text-sm text-green-600 mt-2 flex items-center"
}

import { computed, ref, watch } from 'vue'
import type { BankingCodeType, ValidationRule, BankingInfoFieldProps } from '../../types/sellerRegister'

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'validation-changed', field: string, isValid: boolean): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BankingInfoField',
  props: {
    label: { default: 'Code bancaire ou fiscal' },
    type: {},
    required: { type: Boolean, default: false },
    helpText: { default: 'Saisissez votre numéro' },
    modelValue: {}
  },
  emits: ["update:modelValue", "validation-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Règles de validation par type
const validationRules: Record<BankingCodeType, ValidationRule> = {
  iban: {
    regex: /^FR[0-9]{2}(?: ?[A-Z0-9]{4}){5} ?[A-Z0-9]{3}$/,
    placeholder: 'FR76 3000 1007 9416 0000 0000 000',
    errorMessage: 'Format IBAN invalide. Exemple : FR76 3000 1007 9416 0000 0000 000',
    maxLength: 27,
    minLength: 27
  },
  tva: {
    regex: /^[A-Z]{2}[0-9]{11}$/,
    placeholder: 'FR12345678901',
    errorMessage: 'Format TVA invalide. Exemple : FR12345678901',
    maxLength: 13,
    minLength: 13
  },
  bic: {
    regex: /^[A-Z0-9]{8}([A-Z0-9]{3})?$/,
    placeholder: 'Code BIC (8 ou 11 caractères)',
    errorMessage: 'Format BIC invalide. Le BIC doit contenir 8 ou 11 caractères alphanumériques.',
    maxLength: 11,
    minLength: 8
  }
}

// État local
const inputValue = ref(props.modelValue)
const hasError = ref(false)
const isValid = ref(false)

// Règle de validation actuelle
const validationRule = computed(() => validationRules[props.type])

// Validation de l'input
const validateInput = () => {
  const value = inputValue.value.trim()
  
  if (!value) {
    hasError.value = false
    isValid.value = false
    // Pour les champs requis, un champ vide n'est pas valide
    emit('validation-changed', props.type, false) // Vide = invalide si requis
    return
  }
  
  // Nettoyage spécifique selon le type
  let cleanValue = value
  if (props.type === 'iban') {
    cleanValue = value.replace(/\s/g, '').toUpperCase()
  } else if (props.type === 'tva' || props.type === 'bic') {
    cleanValue = value.toUpperCase()
  }
  
  // Test de la regex
  const isInputValid = validationRule.value.regex.test(cleanValue)
  
  hasError.value = !isInputValid
  isValid.value = isInputValid
  
  emit('validation-changed', props.type, isInputValid)
}

// Formatage automatique de l'IBAN avec espaces
const formatIban = (value: string) => {
  if (props.type === 'iban') {
    // Supprime tous les espaces existants
    const cleanValue = value.replace(/\s/g, '')
    // Ajoute un espace tous les 4 caractères
    return cleanValue.replace(/(.{4})/g, '$1 ').trim()
  }
  return value
}

// Gestion de la saisie avec formatage automatique
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value
  
  if (props.type === 'iban') {
    // Pour l'IBAN, on permet de taper jusqu'à 27 caractères + espaces
    const cleanValue = value.replace(/\s/g, '')
    
    // Blocage strict : si on a exactement 27 caractères, on ne peut plus taper
    if (cleanValue.length >= 27) {
      // On force la valeur formatée et on empêche toute modification
      const finalFormatted = formatIban(cleanValue.substring(0, 27))
      inputValue.value = finalFormatted
      return
    }
    
    // Applique le formatage seulement si on a au moins 4 caractères
    if (cleanValue.length >= 4) {
      const formattedValue = formatIban(value)
      inputValue.value = formattedValue
    } else {
      inputValue.value = value
    }
  } else if (props.type === 'tva') {
    // Pour la TVA, on permet de taper jusqu'à 13 caractères
    const cleanValue = value.replace(/\s/g, '')
    
    if (cleanValue.length > 13) {
      // Si on dépasse 13 caractères, on bloque
      return
    }
    
    // Blocage strict : si on a exactement 13 caractères, on ne peut plus taper
    if (cleanValue.length >= 13) {
      // On force la valeur et on empêche toute modification
      inputValue.value = cleanValue.toUpperCase()
      return
    }
    
    inputValue.value = value.toUpperCase()
  } else if (props.type === 'bic') {
    // Pour le BIC français, on permet de taper jusqu'à 11 caractères
    const cleanValue = value.replace(/\s/g, '')
    
    if (cleanValue.length > 11) {
      // Si on dépasse 11 caractères, on bloque
      return
    }
    
    // Blocage strict : si on a exactement 11 caractères, on ne peut plus taper
    if (cleanValue.length >= 11) {
      // On force la valeur et on empêche toute modification
      inputValue.value = cleanValue.toUpperCase()
      return
    }
    
    // Permet la saisie entre 8 et 11 caractères
    inputValue.value = value.toUpperCase()
  }
  
  // Déclenche la validation
  validateInput()
}

// Synchronisation avec le v-model
watch(inputValue, (newValue) => {
  emit('update:modelValue', newValue)
})

// Synchronisation depuis le v-model externe
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue
  if (newValue) {
    validateInput()
  }
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("label", _hoisted_2, [
      _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
      (_ctx.required)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _withDirectives(_createElementVNode("input", {
        type: "text",
        placeholder: validationRule.value.placeholder,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputValue).value = $event)),
        onInput: handleInput,
        onBlur: validateInput,
        maxlength: props.type === 'iban' ? 35 : props.type === 'tva' ? 13 : props.type === 'bic' ? 11 : validationRule.value.maxLength,
        class: _normalizeClass(["w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200", hasError.value ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'])
      }, null, 42, _hoisted_5), [
        [_vModelText, inputValue.value]
      ]),
      (hasError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_6, [
            _cache[1] || (_cache[1] = _createElementVNode("i", { class: "fas fa-exclamation-circle mr-2" }, null, -1)),
            _createTextVNode(" " + _toDisplayString(validationRule.value.errorMessage), 1)
          ]))
        : (isValid.value && inputValue.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, [
              _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-check-circle mr-2" }, null, -1)),
              _createTextVNode(" Format " + _toDisplayString(props.type.toUpperCase()) + " valide ", 1)
            ]))
          : _createCommentVNode("", true)
    ])
  ]))
}
}

})